import { useCallback, useEffect, useRef, useState } from "react";

export interface TableColumn<T> {
  key: string;
  header: string;
  render?: (row: T) => React.ReactNode;
}

export interface TableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  loading?: boolean;
  emptyMessage?: string;
  containerClassName?: string;
  stickyHeader?: boolean;
  floatingHorizontalScrollbar?: boolean;
  onRowClick?: (row: T) => void;
}

interface FloatingScrollbarState {
  visible: boolean;
  left: number;
  width: number;
  contentWidth: number;
}

export function Table<T extends object>({
  columns,
  data,
  loading = false,
  emptyMessage = "No hay datos",
  containerClassName = "",
  stickyHeader = false,
  floatingHorizontalScrollbar = false,
  onRowClick,
}: TableProps<T>) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const floatingScrollbarRef = useRef<HTMLDivElement>(null);
  const [floatingScrollbar, setFloatingScrollbar] = useState<FloatingScrollbarState>({
    visible: false,
    left: 0,
    width: 0,
    contentWidth: 0,
  });
  const headerClassName = `bg-gray-50 ${stickyHeader ? "sticky top-0 z-10 shadow-sm" : ""}`;

  const updateFloatingScrollbar = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!floatingHorizontalScrollbar || !container) {
      setFloatingScrollbar((current) =>
        current.visible ? { ...current, visible: false } : current
      );
      return;
    }

    const rect = container.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight;
    const left = Math.max(0, rect.left);
    const width = Math.max(0, Math.min(container.clientWidth, viewportWidth - left));
    const visible =
      container.scrollWidth > container.clientWidth + 1 &&
      rect.top < viewportHeight &&
      rect.bottom > viewportHeight &&
      width > 0;
    const next = {
      visible,
      left,
      width,
      contentWidth: container.scrollWidth,
    };

    setFloatingScrollbar((current) =>
      current.visible === next.visible &&
      current.left === next.left &&
      current.width === next.width &&
      current.contentWidth === next.contentWidth
        ? current
        : next
    );
  }, [floatingHorizontalScrollbar]);

  useEffect(() => {
    if (!floatingHorizontalScrollbar) return;

    const frame = window.requestAnimationFrame(updateFloatingScrollbar);
    const container = scrollContainerRef.current;
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(updateFloatingScrollbar);
    if (container && observer) {
      observer.observe(container);
      const table = container.querySelector("table");
      if (table) observer.observe(table);
    }

    window.addEventListener("resize", updateFloatingScrollbar);
    window.addEventListener("scroll", updateFloatingScrollbar, true);
    return () => {
      window.cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener("resize", updateFloatingScrollbar);
      window.removeEventListener("scroll", updateFloatingScrollbar, true);
    };
  }, [columns.length, data.length, floatingHorizontalScrollbar, loading, updateFloatingScrollbar]);

  useEffect(() => {
    if (!floatingScrollbar.visible) return;
    const container = scrollContainerRef.current;
    const scrollbar = floatingScrollbarRef.current;
    if (container && scrollbar) scrollbar.scrollLeft = container.scrollLeft;
  }, [floatingScrollbar.contentWidth, floatingScrollbar.visible]);

  const handleContainerScroll = () => {
    const container = scrollContainerRef.current;
    const scrollbar = floatingScrollbarRef.current;
    if (container && scrollbar && scrollbar.scrollLeft !== container.scrollLeft) {
      scrollbar.scrollLeft = container.scrollLeft;
    }
    updateFloatingScrollbar();
  };

  const handleFloatingScrollbarScroll = () => {
    const container = scrollContainerRef.current;
    const scrollbar = floatingScrollbarRef.current;
    if (container && scrollbar && container.scrollLeft !== scrollbar.scrollLeft) {
      container.scrollLeft = scrollbar.scrollLeft;
    }
  };

  const floatingScrollbarElement =
    floatingHorizontalScrollbar && floatingScrollbar.visible ? (
      <div
        ref={floatingScrollbarRef}
        role="region"
        aria-label="Desplazamiento horizontal de la tabla"
        className="fixed bottom-0 z-40 h-5 overflow-x-scroll overflow-y-hidden border-y border-gray-300 bg-white shadow-[0_-3px_10px_rgba(0,0,0,0.12)]"
        style={{ left: floatingScrollbar.left, width: floatingScrollbar.width }}
        onScroll={handleFloatingScrollbarScroll}
      >
        <div style={{ width: floatingScrollbar.contentWidth, height: 1 }} />
      </div>
    ) : null;

  if (loading) {
    return (
      <>
        <div
          ref={scrollContainerRef}
          className={`overflow-x-auto rounded-lg border border-gray-200 bg-white ${containerClassName}`}
          onScroll={handleContainerScroll}
        >
          <table className="min-w-full divide-y divide-gray-200">
            <thead className={headerClassName}>
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                  >
                    {col.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {[1, 2, 3, 4, 5].map((i) => (
                <tr key={i}>
                  {columns.map((col) => (
                    <td key={col.key} className="px-4 py-3">
                      <div className="h-5 w-3/4 animate-pulse rounded bg-gray-200" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {floatingScrollbarElement}
      </>
    );
  }

  if (data.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white py-12 text-center text-gray-500">
        {emptyMessage}
      </div>
    );
  }

  return (
    <>
      <div
        ref={scrollContainerRef}
        className={`overflow-x-auto rounded-lg border border-gray-200 bg-white ${containerClassName}`}
        onScroll={handleContainerScroll}
      >
        <table className="min-w-full divide-y divide-gray-200">
          <thead className={headerClassName}>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {data.map((row, idx) => (
              <tr
                key={idx}
                className={`hover:bg-gray-50 ${
                  onRowClick
                    ? "cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#1A3AB8]"
                    : ""
                }`}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                onKeyDown={
                  onRowClick
                    ? (event) => {
                        if (event.target !== event.currentTarget) return;
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          onRowClick(row);
                        }
                      }
                    : undefined
                }
                tabIndex={onRowClick ? 0 : undefined}
              >
                {columns.map((col) => (
                  <td key={col.key} className="px-4 py-3 text-sm text-[#2C2C2C]">
                    {col.render
                      ? col.render(row)
                      : String((row as Record<string, unknown>)[col.key] ?? "")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {floatingScrollbarElement}
    </>
  );
}
